import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  Tabs,
  useToast,
  type Column,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  useAddApprovalNote,
  useApprovals,
  useDecideApproval,
} from '@/features/billing/api';
import { formatRM } from '@/features/billing/money';
import {
  APPROVAL_KIND_LABEL,
  METHOD_LABEL,
  REASON_LABEL,
  type Approval,
} from '@/features/billing/types';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import { useOptionalUrlState, useUrlState } from '@/lib/url-state';
import local from '@/pages/finance/finance.module.css';
import styles from '@/pages/queue/queue.module.css';
import { CLINIC_TZ } from '@/lib/clinic-time';
import gov from './governance.module.css';

const NOTE_MAX = 500;
const when = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { timeZone: CLINIC_TZ });

type Tab = 'waiting' | 'decided';

/**
 * Admin v2 "Approvals" (artifacts #176–177): refunds, voids, discounts and
 * large paid-outs requested at the counter. Nobody can approve their own
 * request; a rejection needs a note.
 */
export function ApprovalsPage() {
  const { hasPermission, me } = useSession();
  const [tab, setTab] = useUrlState<Tab>('tab', 'waiting', [
    'waiting',
    'decided',
  ]);
  const [openId, setOpenId] = useOptionalUrlState('drawer');
  const { data, isLoading, error, refetch } = useApprovals(tab);
  const open = data?.find((approval) => approval.id === openId) ?? null;
  const decidePermission = (a: Approval) =>
    hasPermission(a.kind === 'void' ? 'bills:void' : 'approvals:decide');
  const canDecide = (a: Approval) =>
    a.status === 'pending' &&
    a.requestedBy?.id !== me?.id &&
    decidePermission(a);

  const columns: Column<Approval>[] = [
    {
      key: 'kind',
      header: 'Request',
      render: (a) => <Badge>{APPROVAL_KIND_LABEL[a.kind]}</Badge>,
    },
    {
      key: 'what',
      header: 'Bill / item',
      render: (a) =>
        a.bill ? (
          <>
            <p className="mono">{a.bill.number}</p>
            <p className={styles.sub}>{a.bill.patient.fullName}</p>
          </>
        ) : (
          (a.paidOut?.reason ?? '—')
        ),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (a) => formatRM(a.amountSen),
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (a) => (
        <>
          <p>{REASON_LABEL[a.reason] ?? a.reason}</p>
          <p className={styles.sub}>
            {a.requestedBy?.fullName ?? '—'} ·{' '}
            {new Date(a.requestedAt).toLocaleString('en-GB', {
              timeZone: CLINIC_TZ,
            })}
          </p>
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (a) =>
        a.status === 'pending' ? (
          <Badge tone="warning">Waiting</Badge>
        ) : (
          <>
            <Badge tone={a.status === 'approved' ? 'success' : 'danger'}>
              {a.status === 'approved' ? 'Approved' : 'Rejected'}
            </Badge>
            <p className={styles.sub}>
              {a.decidedBy?.fullName ?? '—'}
              {a.decisionNote ? ` · ${a.decisionNote}` : ''}
            </p>
            {!!a.correctionNotes?.length && (
              <p className={styles.sub}>
                {a.correctionNotes.length === 1
                  ? '1 correction note'
                  : `${a.correctionNotes.length} correction notes`}
              </p>
            )}
          </>
        ),
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (a) => (
        <Button size="sm" onClick={() => setOpenId(a.id)}>
          {a.status === 'pending' ? 'Decide' : 'Open'}
        </Button>
      ),
    },
  ];

  return (
    <PageContainer>
      <p className={local.note}>
        Refunds, voids, discounts and large paid-outs are requested at the
        counter and approved here by someone else. Nobody can approve their own
        request. Every decision is in the audit log.
      </p>
      <Card title="Approvals" flush>
        <Tabs
          label="Approvals"
          value={tab}
          onChange={setTab}
          items={[
            {
              value: 'waiting',
              label: 'Waiting',
              count: tab === 'waiting' ? data?.length : undefined,
            },
            { value: 'decided', label: 'Decided' },
          ]}
        />
        <DataTable
          caption="Approvals"
          columns={columns}
          rows={data}
          rowKey={(a) => `${a.kind}:${a.id}`}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          empty={
            <EmptyState
              title={
                tab === 'waiting' ? 'Nothing waiting' : 'Nothing decided yet'
              }
              body="Refunds, voids, discounts and large paid-outs requested at the counter appear here."
            />
          }
        />
      </Card>
      {open && open.status === 'pending' && (
        <DecideDialog
          approval={open}
          canDecide={canDecide(open)}
          onClose={() => setOpenId(null)}
        />
      )}
      {open && open.status !== 'pending' && (
        <DecidedDialog
          approval={open}
          canAddNote={decidePermission(open)}
          onClose={() => setOpenId(null)}
        />
      )}
    </PageContainer>
  );
}

function DecideDialog({
  approval: a,
  canDecide,
  onClose,
}: {
  approval: Approval;
  canDecide: boolean;
  onClose: () => void;
}) {
  const decide = useDecideApproval();
  const toast = useToast();
  const [note, setNote] = useState('');
  const go = (approve: boolean) =>
    decide
      .mutateAsync({
        id: a.id,
        kind: a.kind,
        approve,
        note: note.trim() || undefined,
      })
      .then(() => {
        toast.show({
          tone: 'success',
          title: approve
            ? `${APPROVAL_KIND_LABEL[a.kind]} approved`
            : 'Sent back with your note',
        });
        onClose();
      })
      .catch((err: unknown) =>
        // RL-1822: a paid-out or cash refund leaves the drawer when
        // approved, so a shift must be open at the counter.
        isApiError(err) && err.code === 'NO_OPEN_SHIFT'
          ? toast.show({
              tone: 'error',
              title: 'Open the cash drawer first',
              body: 'The cash leaves the drawer when it is approved. Open a shift at this branch’s counter, then approve.',
              error: err,
            })
          : toast.show({
              tone: 'error',
              title: isApiError(err)
                ? (err.problem?.title ?? 'Not done')
                : 'Not done',
              body: isApiError(err) ? err.message : undefined,
              error: err,
            }),
      );
  const effect =
    a.kind === 'refund'
      ? a.refundMethod === 'cash'
        ? 'Approving takes the cash refund out of the drawer now, so the drawer must be open.'
        : `Approving lets the counter refund it back by ${a.refundMethod ? METHOD_LABEL[a.refundMethod] : 'the original method'} on the terminal.`
      : a.kind === 'discount'
        ? 'Approving takes it off what the patient pays.'
        : a.kind === 'void'
          ? 'Approving voids the bill.'
          : 'Approving takes the cash out of the drawer now: it counts in the shift open at the counter, so the drawer must be open.';
  return (
    <Dialog
      open
      variant="modal"
      title={`${APPROVAL_KIND_LABEL[a.kind]} to decide`}
      onClose={onClose}
      footer={
        canDecide ? (
          <>
            <Button
              disabled={!note.trim()}
              loading={decide.isPending}
              onClick={() => void go(false)}
            >
              Reject
            </Button>
            <Button
              variant={a.kind === 'void' ? 'danger' : 'primary'}
              loading={decide.isPending}
              onClick={() => void go(true)}
            >
              Approve
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      <div className={styles.drawer}>
        <dl className={styles.rows}>
          {a.bill && (
            <>
              <dt>Bill</dt>
              <dd>
                <span className="mono">{a.bill.number}</span> ·{' '}
                {a.bill.patient.fullName} · paid {formatRM(a.bill.paidSen)}
              </dd>
            </>
          )}
          <dt>Amount</dt>
          <dd>
            <strong>{formatRM(a.amountSen)}</strong>
          </dd>
          <dt>Reason</dt>
          <dd>
            {REASON_LABEL[a.reason] ?? a.reason}
            {a.note ? ` · ${a.note}` : ''}
          </dd>
          <dt>Requested by</dt>
          <dd>{a.requestedBy?.fullName ?? '—'}</dd>
        </dl>
        {canDecide ? (
          <>
            <p className={styles.sub}>{effect}</p>
            <Field label="Note" hint="A rejection needs a note.">
              <Input value={note} onChange={(ev) => setNote(ev.target.value)} />
            </Field>
          </>
        ) : (
          <p className={styles.warnNote}>
            Nobody can approve their own request. The clinic manager or the
            Super admin decides.
          </p>
        )}
      </div>
    </Dialog>
  );
}

/**
 * RL-1815 (decision D28): a decided request. The decision is final; a
 * correction note can be added beside it (append-only, oldest first).
 */
function DecidedDialog({
  approval: a,
  canAddNote,
  onClose,
}: {
  approval: Approval;
  canAddNote: boolean;
  onClose: () => void;
}) {
  const add = useAddApprovalNote();
  const toast = useToast();
  const [text, setText] = useState('');
  const notes = a.correctionNotes ?? [];
  const trimmed = text.trim();
  const save = () =>
    add
      .mutateAsync({ id: a.id, text: trimmed })
      .then(() => {
        toast.show({ tone: 'success', title: 'Correction note added' });
        setText('');
      })
      .catch((err: unknown) =>
        toast.show({
          tone: 'error',
          title: isApiError(err)
            ? (err.problem?.title ?? 'Not added')
            : 'Not added',
          body: isApiError(err) ? err.message : undefined,
          error: err,
        }),
      );
  return (
    <Dialog
      open
      variant="modal"
      title={`${APPROVAL_KIND_LABEL[a.kind]} · ${a.status === 'approved' ? 'approved' : 'rejected'}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          {canAddNote && (
            <Button
              variant="primary"
              disabled={!trimmed || trimmed.length > NOTE_MAX}
              loading={add.isPending}
              onClick={() => void save()}
            >
              Add correction note
            </Button>
          )}
        </>
      }
    >
      <div className={styles.drawer}>
        <dl className={styles.rows}>
          {a.bill && (
            <>
              <dt>Bill</dt>
              <dd>
                <span className="mono">{a.bill.number}</span> ·{' '}
                {a.bill.patient.fullName}
              </dd>
            </>
          )}
          <dt>Amount</dt>
          <dd>
            <strong>{formatRM(a.amountSen)}</strong>
          </dd>
          <dt>Reason</dt>
          <dd>
            {REASON_LABEL[a.reason] ?? a.reason}
            {a.note ? ` · ${a.note}` : ''}
          </dd>
          <dt>Requested by</dt>
          <dd>{a.requestedBy?.fullName ?? '—'}</dd>
          <dt>Decision</dt>
          <dd>
            <Badge tone={a.status === 'approved' ? 'success' : 'danger'}>
              {a.status === 'approved' ? 'Approved' : 'Rejected'}
            </Badge>{' '}
            {a.decidedBy?.fullName ?? '—'}
            {a.decidedAt ? ` · ${when(a.decidedAt)}` : ''}
            {a.decisionNote ? ` · ${a.decisionNote}` : ''}
          </dd>
        </dl>
        <p className={styles.sub}>
          Decisions are final. A correction note is kept beside the decision and
          in the audit log; it does not change it.
        </p>
        <h3 className={gov.sectionTitle}>Correction notes</h3>
        {notes.length ? (
          <ul className={gov.notes} aria-label="Correction notes">
            {notes.map((n) => (
              <li key={n.id} className={gov.note}>
                <p>{n.text}</p>
                <p className={gov.noteMeta}>
                  {n.by?.fullName ?? '—'} · {when(n.at)}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.sub}>No correction notes.</p>
        )}
        {canAddNote && (
          <Field
            label="Correction note"
            hint={`What was wrong, in plain words (up to ${NOTE_MAX} characters).`}
            error={
              trimmed.length > NOTE_MAX
                ? `Keep it to ${NOTE_MAX} characters.`
                : undefined
            }
          >
            <textarea
              className={gov.textarea}
              value={text}
              onChange={(ev) => setText(ev.target.value)}
            />
          </Field>
        )}
      </div>
    </Dialog>
  );
}
